import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { 
  Activity, LogOut, Clock, AlertCircle, CheckCircle2, BarChart3, FileText, 
  ArrowRight, TrendingUp, AlertTriangle, Timer, Play, Users, MessageSquarePlus, XCircle, QrCode, Menu, X, UserPlus, Copy, Share2, Search, Wallet
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import SLAConfigPanel from "@/components/SLAConfigPanel";
import NotificationBell from "@/components/NotificationBell";
import SmartLinkPanel from "@/components/SmartLinkPanel";
import PrivatePracticePanel from "@/components/PrivatePracticePanel";
import MyEarningsPanel from "@/components/MyEarningsPanel";
import DoctorOnboardingTour, { shouldShowDoctorOnboarding } from "@/components/DoctorOnboardingTour";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DoctorDashboard = ({ user }) => {
  const navigate = useNavigate();
  const [consults, setConsults] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  // Sort order for the "Respondidas" tab: 'recent' (más recientes primero) or 'oldest'.
  const [sortOrder, setSortOrder] = useState('recent');
  const [showSLAConfig, setShowSLAConfig] = useState(false);
  const [showSmartLink, setShowSmartLink] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [myInstitutions, setMyInstitutions] = useState([]);
  const [institutionFilter, setInstitutionFilter] = useState('all');
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showOnboardingTour, setShowOnboardingTour] = useState(false);
  const [showPrivatePractice, setShowPrivatePractice] = useState(false);
  const [showEarnings, setShowEarnings] = useState(false);
  const [privatePractice, setPrivatePractice] = useState(null);

  // Inbox filters (client-side, over already-loaded consults):
  //  - patient search: name or DNI, case-insensitive substring match.
  //  - category filter: multi-select chips. Empty set = show all categories.
  //  - red flags only: show only consults with at least one active red flag.
  const [patientSearch, setPatientSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState([]);
  const [redFlagsOnly, setRedFlagsOnly] = useState(false);

  // Active tenant for the dashboard's branding (logo, name, contact info).
  // Rule:
  //   - When "Todas las instituciones" is selected, we fall back to the doctor's
  //     primary institution (user.institution_id). This avoids ever showing the
  //     platform brand ("Thales Health") in the header of a Fleni-hired doctor,
  //     which would look odd from the client's perspective.
  //   - When a specific tenant is selected in the dropdown, that one takes over.
  const activeTenant = useMemo(() => {
    if (!myInstitutions.length) return null;
    if (institutionFilter !== 'all') {
      return myInstitutions.find(i => i.id === institutionFilter) || myInstitutions[0];
    }
    return myInstitutions.find(i => i.id === user?.institution_id) || myInstitutions[0];
  }, [myInstitutions, institutionFilter, user?.institution_id]);

  // Persist the last selected tenant across reloads so the branding stays consistent
  // for the doctor within the same session (feels less "flashy").
  useEffect(() => {
    if (institutionFilter && institutionFilter !== 'all') {
      try { localStorage.setItem('doctor_active_tenant', institutionFilter); } catch (_) { /* storage disabled */ }
    } else if (institutionFilter === 'all') {
      try { localStorage.removeItem('doctor_active_tenant'); } catch (_) { /* storage disabled */ }
    }
  }, [institutionFilter]);

  useEffect(() => {
    if (myInstitutions.length > 1 && institutionFilter === 'all') {
      try {
        const persisted = localStorage.getItem('doctor_active_tenant');
        if (persisted && myInstitutions.some(i => i.id === persisted)) {
          setInstitutionFilter(persisted);
        }
      } catch (_) { /* storage disabled */ }
    }
  }, [myInstitutions, institutionFilter]);

  const loadPrivatePractice = useCallback(async () => {
    if (user?.role !== 'MEDICO') return;
    try {
      const token = localStorage.getItem('auth_token');
      const res = await fetch(`${API_URL}/api/doctors/me/private-practice`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) setPrivatePractice(await res.json());
    } catch (err) {
      console.error('Error loading private practice:', err);
    }
  }, [user]);

  useEffect(() => {
    loadPrivatePractice();
  }, [loadPrivatePractice]);

  // Allow child components (SmartLinkPanel) to open the Consultorio Privado panel
  // via a global event. Used by the "activate private practice" CTA inside the
  // links modal so the doctor can jump directly to activation.
  useEffect(() => {
    const handler = () => setShowPrivatePractice(true);
    window.addEventListener('open-private-practice-panel', handler);
    return () => window.removeEventListener('open-private-practice-panel', handler);
  }, []);

  // Show onboarding tour on first dashboard visit per user.
  // We delay slightly so the dashboard finishes its first render and the
  // tooltip targets (smart-link-btn, consults-grid, etc.) exist in the DOM.
  useEffect(() => {
    if (shouldShowDoctorOnboarding(user)) {
      const t = setTimeout(() => setShowOnboardingTour(true), 600);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [user]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      
      let queryParam = '';
      if (filter === 'all') queryParam = '?all=true';
      else if (filter === 'responded') queryParam = '?status=Responded,Resolved,CLOSED_TIMEOUT';
      if (institutionFilter !== 'all') {
        queryParam += (queryParam ? '&' : '?') + `institution_filter=${institutionFilter}`;
      }
      const consultsResponse = await fetch(`${API_URL}/api/consults${queryParam}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      // If the session is no longer valid, never render a "logged-in but empty"
      // dashboard — send the doctor to re-login so they get a fresh token.
      if (consultsResponse.status === 401 || consultsResponse.status === 403) {
        localStorage.removeItem('auth_token');
        toast.error("Tu sesión expiró. Volvé a ingresar.");
        navigate('/');
        return;
      }

      if (consultsResponse.ok) {
        const consultsData = await consultsResponse.json();
        setConsults(consultsData);
      }

      const statsResponse = await fetch(`${API_URL}/api/stats${institutionFilter !== 'all' ? `?institution_filter=${institutionFilter}` : ''}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (statsResponse.ok) {
        const statsData = await statsResponse.json();
        setStats(statsData);
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error("Error al cargar datos");
    } finally {
      setLoading(false);
    }
  }, [filter, institutionFilter, navigate]);

  // Load institutions list
  useEffect(() => {
    const loadInstitutions = async () => {
      try {
        const token = localStorage.getItem('auth_token');
        const res = await fetch(`${API_URL}/api/doctors/my-institutions`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setMyInstitutions(data);
        }
      } catch (err) {
        console.error('Error loading institutions:', err);
      }
    };
    loadInstitutions();
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Auto-refresh: poll every 30s while the tab is visible, and refresh
  // immediately when the tab regains focus. Pauses while hidden to save bandwidth.
  useEffect(() => {
    let intervalId = null;

    const startPolling = () => {
      if (intervalId == null) {
        intervalId = setInterval(loadData, 30000);
      }
    };
    const stopPolling = () => {
      if (intervalId != null) {
        clearInterval(intervalId);
        intervalId = null;
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        loadData(); // refresh as soon as the doctor comes back
        startPolling();
      } else {
        stopPolling();
      }
    };

    if (document.visibilityState === 'visible') startPolling();
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      stopPolling();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [loadData]);

  const handleLogout = () => {
    localStorage.removeItem('auth_token');
    navigate('/');
    toast.success("Sesión cerrada");
  };

  // Calcular estado de prioridad de una consulta (semáforo)
  const getPriorityStatus = (consult) => {
    if (!consult.sla_deadline || consult.status !== 'Paid') return null;
    
    const now = new Date();
    const deadline = new Date(consult.sla_deadline);
    const hoursRemaining = (deadline - now) / (1000 * 60 * 60);
    
    if (hoursRemaining < 0) {
      return { color: 'red', label: 'Vencida', className: 'border-l-4 border-l-red-500 bg-red-50' };
    } else if (hoursRemaining < 6) {
      return { color: 'yellow', label: 'Por vencer', className: 'border-l-4 border-l-amber-500 bg-amber-50' };
    } else {
      return { color: 'green', label: 'En tiempo', className: 'border-l-4 border-l-green-500' };
    }
  };

  const getStatusBadge = (status) => {
    const configs = {
      'Paid': { label: 'Pendiente', className: 'bg-amber-50 text-amber-700 border-amber-200' },
      'InReview': { label: 'En Revisión', className: 'bg-blue-50 text-blue-700 border-blue-200' },
      'Responded': { label: 'Respondida', className: 'bg-green-50 text-green-700 border-green-200' },
      'Resolved': { label: 'Resuelta', className: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
      'ESCALATED': { label: 'Escalada', className: 'bg-purple-50 text-purple-700 border-purple-200' },
      'RejectedByTriage': { label: 'Rechazada', className: 'bg-red-50 text-red-700 border-red-200' },
      'AWAITING_INFO': { label: 'Esperando Info', className: 'bg-orange-50 text-orange-700 border-orange-200' },
      'INFO_RECEIVED': { label: 'Info Recibida', className: 'bg-violet-50 text-violet-700 border-violet-200' },
      'CLOSED_TIMEOUT': { label: 'Cerrada (Timeout)', className: 'bg-slate-100 text-slate-600 border-slate-200' }
    };
    const config = configs[status] || { label: status, className: '' };
    return <Badge className={`${config.className} border`}>{config.label}</Badge>;
  };

  const getTimeRemaining = (deadline) => {
    if (!deadline) return null;
    const now = new Date();
    const deadlineDate = new Date(deadline);
    const diffHours = Math.floor((deadlineDate - now) / (1000 * 60 * 60));
    
    if (diffHours < 0) {
      return <span className="text-red-600 font-semibold">Vencido</span>;
    } else if (diffHours < 6) {
      return <span className="text-amber-600 font-semibold">{diffHours}h restantes</span>;
    } else if (diffHours < 24) {
      return <span className="text-slate-600">{diffHours}h restantes</span>;
    } else {
      return <span className="text-slate-500">{Math.floor(diffHours / 24)}d {diffHours % 24}h</span>;
    }
  };

  const getCategoryLabel = (category) => {
    const labels = {
      'medicacion': 'Tratamiento',
      'recetas': 'Receta',
      'resultados': 'Resultados',
      'seguimiento': 'Seguimiento',
      'documentacion': 'Documentación',
      'otra': 'Otra consulta'
    };
    return labels[category] || category || 'Sin categoría';
  };

  // Ordenar consultas. En la pestaña "Respondidas" se ordena por fecha de
  // respuesta (más recientes / más antiguas) para encontrar rápido lo recién
  // respondido. En las demás, prioridad: vencidas primero, luego por SLA.
  const sortedConsults = [...consults].sort((a, b) => {
    if (filter === 'responded') {
      const da = new Date(a.responded_at || a.created_at).getTime();
      const db = new Date(b.responded_at || b.created_at).getTime();
      return sortOrder === 'oldest' ? da - db : db - da;
    }
    if (a.status !== 'Paid' && b.status === 'Paid') return 1;
    if (a.status === 'Paid' && b.status !== 'Paid') return -1;
    if (a.sla_deadline && b.sla_deadline) {
      return new Date(a.sla_deadline) - new Date(b.sla_deadline);
    }
    return 0;
  });

  // Apply inbox filters (search + category + red flags) on top of the ordered list.
  // All filters are pure-client — no backend round-trip.
  const consultHasActiveRedFlag = (c) => {
    const rf = c.red_flags;
    if (!rf || typeof rf !== 'object') return false;
    return Object.values(rf).some(Boolean);
  };
  const normalizedSearch = patientSearch.trim().toLowerCase();
  const filteredConsults = sortedConsults.filter((c) => {
    if (normalizedSearch) {
      const name = (c.patient_name || '').toLowerCase();
      const dni = (c.patient_dni || '').toString().toLowerCase();
      if (!name.includes(normalizedSearch) && !dni.includes(normalizedSearch)) return false;
    }
    if (categoryFilter.length > 0 && !categoryFilter.includes(c.category)) return false;
    if (redFlagsOnly && !consultHasActiveRedFlag(c)) return false;
    return true;
  });

  const toggleCategoryFilter = (cat) => {
    setCategoryFilter((prev) => prev.includes(cat) ? prev.filter(c => c !== cat) : [...prev, cat]);
  };
  const clearInboxFilters = () => {
    setPatientSearch('');
    setCategoryFilter([]);
    setRedFlagsOnly(false);
  };
  const hasActiveInboxFilter = Boolean(patientSearch) || categoryFilter.length > 0 || redFlagsOnly;

  const CATEGORY_OPTIONS = [
    { id: 'recetas', label: 'Recetas' },
    { id: 'seguimiento', label: 'Seguimiento' },
    { id: 'resultados', label: 'Resultados' },
    { id: 'medicacion', label: 'Tratamiento' },
    { id: 'documentacion', label: 'Documentación' },
    { id: 'otra', label: 'Otra' },
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Cargando...</p>
        </div>
      </div>
    );
  }

  const pendingCount = stats?.pending || 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <nav className="border-b border-slate-200 bg-white/70 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-14 sm:h-16">
            {/* Logo — reactive to the active tenant. Falls back to Fleni's asset while
                the institutions endpoint is still loading, so the header never blanks out. */}
            <div className="flex items-center space-x-2 sm:space-x-3">
              <img
                src={activeTenant?.branding?.logo_url || "/logo-fleni.png"}
                alt={activeTenant?.name || "Fleni"}
                className="h-8 sm:h-10 object-contain"
                data-testid="dashboard-tenant-logo"
              />
            </div>
            
            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-3">
              <div className="text-sm text-right mr-2">
                <p className="font-semibold text-primary">{user.name}</p>
                <p className="text-slate-500 text-xs">{user.role}</p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowSLAConfig(true)}
                data-testid="response-time-config-btn"
                title="Configurar tiempos de respuesta"
              >
                <Clock className="h-4 w-4 mr-1" />
                Tiempos
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowSmartLink(true)}
                data-testid="smart-link-btn"
                title="Mis enlaces para compartir"
              >
                <QrCode className="h-4 w-4 mr-1" />
                Mis enlaces
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowInviteModal(true)}
                data-testid="invite-colleague-btn"
                title="Invitar a un colega"
              >
                <UserPlus className="h-4 w-4 mr-1" />
                Invitar
              </Button>
              {user.role === 'ADMIN' && (
                <>
                  <Button variant="outline" size="sm" onClick={() => navigate('/admin')} data-testid="admin-panel-btn">
                    <BarChart3 className="h-4 w-4 mr-1" />
                    Admin
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => navigate('/admin/doctors')} data-testid="admin-doctors-btn">
                    <Users className="h-4 w-4 mr-1" />
                    Médicos
                  </Button>
                </>
              )}
              <NotificationBell />
              <Button variant="outline" size="sm" onClick={handleLogout} data-testid="logout-btn">
                <LogOut className="h-4 w-4 mr-1" />
                Salir
              </Button>
            </div>
            
            {/* Mobile Navigation */}
            <div className="flex md:hidden items-center space-x-2">
              <NotificationBell />
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="p-2"
              >
                {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </Button>
            </div>
          </div>
          
          {/* Mobile Menu Dropdown */}
          {mobileMenuOpen && (
            <div className="md:hidden border-t border-slate-200 py-3 space-y-2">
              <div className="px-2 py-2 bg-slate-50 rounded-lg mb-2">
                <p className="font-semibold text-primary text-sm">{user.name}</p>
                <p className="text-slate-500 text-xs">{user.role}</p>
              </div>
              <Button
                variant="ghost"
                className="w-full justify-start"
                onClick={() => { setShowSLAConfig(true); setMobileMenuOpen(false); }}
              >
                <Clock className="h-4 w-4 mr-2" />
                Tiempos de respuesta
              </Button>
              <Button
                variant="ghost"
                className="w-full justify-start"
                onClick={() => { setShowSmartLink(true); setMobileMenuOpen(false); }}
              >
                <QrCode className="h-4 w-4 mr-2" />
                Mis enlaces
              </Button>
              <Button
                variant="ghost"
                className="w-full justify-start"
                onClick={() => { setShowInviteModal(true); setMobileMenuOpen(false); }}
              >
                <UserPlus className="h-4 w-4 mr-2" />
                Invitar colega
              </Button>
              {user.role === 'ADMIN' && (
                <>
                  <Button variant="ghost" className="w-full justify-start" onClick={() => { navigate('/admin'); setMobileMenuOpen(false); }}>
                    <BarChart3 className="h-4 w-4 mr-2" />
                    Panel Admin
                  </Button>
                  <Button variant="ghost" className="w-full justify-start" onClick={() => { navigate('/admin/doctors'); setMobileMenuOpen(false); }}>
                    <Users className="h-4 w-4 mr-2" />
                    Gestionar Médicos
                  </Button>
                </>
              )}
              <div className="border-t border-slate-200 pt-2 mt-2">
                <Button variant="ghost" className="w-full justify-start text-red-600" onClick={handleLogout}>
                  <LogOut className="h-4 w-4 mr-2" />
                  Cerrar sesión
                </Button>
              </div>
            </div>
          )}
        </div>
      </nav>

      <SLAConfigPanel isOpen={showSLAConfig} onClose={() => setShowSLAConfig(false)} />
      <SmartLinkPanel user={user} isOpen={showSmartLink} onClose={() => setShowSmartLink(false)} />
      <PrivatePracticePanel
        user={user}
        isOpen={showPrivatePractice}
        onClose={() => setShowPrivatePractice(false)}
        onChanged={() => loadPrivatePractice()}
      />
      <MyEarningsPanel isOpen={showEarnings} onClose={() => setShowEarnings(false)} />

      {showOnboardingTour && (
        <DoctorOnboardingTour
          user={user}
          onComplete={() => setShowOnboardingTour(false)}
        />
      )}

      {/* Modal Invitar Colega */}
      {showInviteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 relative">
            <button
              onClick={() => setShowInviteModal(false)}
              className="absolute top-4 right-4 text-slate-400 hover:text-slate-700"
            >
              <X className="h-5 w-5" />
            </button>

            <div className="text-center mb-6">
              <div className="h-14 w-14 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <UserPlus className="h-7 w-7 text-emerald-600" />
              </div>
              <h2 className="text-xl font-bold text-slate-900">Invitar colega</h2>
              <p className="text-sm text-slate-500 mt-1">Compartí la plataforma con otro profesional</p>
            </div>

            <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 mb-5">
              <p className="text-sm text-slate-700 leading-relaxed italic">
                "Te comparto una plataforma que estoy usando para ordenar y monetizar las consultas asincronicas con mis pacientes. Vos controlas la carga de trabajo y cada consulta queda registrada y cobrada. Registrate aca:"
              </p>
            </div>

            <div className="space-y-3">
              {/* Botón WhatsApp */}
              <Button
                className="w-full h-12 bg-[#25D366] hover:bg-[#1da851] text-white font-medium"
                onClick={() => {
                  const message = encodeURIComponent(
                    `Te comparto una plataforma que estoy usando para ordenar y monetizar las consultas asincrónicas con mis pacientes. Vos controlás la carga de trabajo y cada consulta queda registrada y cobrada. Registrate acá: ${window.location.origin}/doctor/register`
                  );
                  window.open(`https://wa.me/?text=${message}`, '_blank');
                  setShowInviteModal(false);
                }}
                data-testid="invite-whatsapp-btn"
              >
                <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                Enviar por WhatsApp
              </Button>

              {/* Botón Copiar link */}
              <Button
                variant="outline"
                className="w-full h-12"
                onClick={async () => {
                  const link = `${window.location.origin}/doctor/register`;
                  await navigator.clipboard.writeText(link);
                  toast.success("Link de registro copiado");
                  setShowInviteModal(false);
                }}
                data-testid="invite-copy-link-btn"
              >
                <Copy className="h-4 w-4 mr-2" />
                Copiar link de registro
              </Button>
            </div>

            <p className="text-xs text-slate-400 text-center mt-4">
              Tu colega podra registrarse y solicitar acceso a la plataforma
            </p>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        
        {/* ===== MÉTRICAS PRINCIPALES (4 tarjetas: Totales / Pendientes / Respondidas / Vencidas) ===== */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            {/* Total */}
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-slate-500">Consultas Totales</span>
                <FileText className="h-4 w-4 text-slate-400" />
              </div>
              <p className="text-2xl font-bold text-slate-800">{stats.total_consults}</p>
            </div>
            
            {/* Pendientes */}
            <div className="bg-white rounded-xl border border-slate-200 p-4 border-l-4 border-l-amber-400">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-slate-500">Pendientes</span>
                <Clock className="h-4 w-4 text-amber-500" />
              </div>
              <p className="text-2xl font-bold text-amber-600">{stats.pending}</p>
            </div>
            
            {/* Respondidas */}
            <div className="bg-white rounded-xl border border-slate-200 p-4 border-l-4 border-l-green-400">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-slate-500">Respondidas</span>
                <CheckCircle2 className="h-4 w-4 text-green-500" />
              </div>
              <p className="text-2xl font-bold text-green-600">{stats.responded}</p>
            </div>

            {/* Vencidas */}
            <div className="bg-white rounded-xl border border-slate-200 p-4 border-l-4 border-l-red-400">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-slate-500">Vencidas</span>
                <AlertTriangle className="h-4 w-4 text-red-500" />
              </div>
              <p className="text-2xl font-bold text-red-600">{stats.overdue || 0}</p>
            </div>
          </div>
        )}

        {/* ===== CONSULTORIO PRIVADO ===== */}
        {privatePractice?.linked && !privatePractice.active && (
          <div className="bg-gradient-to-r from-[#1d366b] to-[#2a4a8f] rounded-xl p-5 mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-testid="private-practice-banner">
            <div className="text-white">
              <p className="font-semibold text-base">Activá tu consultorio privado</p>
              <p className="text-sm text-white/80 mt-0.5">
                Atendé a tus pacientes particulares con tu propio enlace y precio, en una bandeja separada.
              </p>
            </div>
            <Button
              onClick={() => setShowPrivatePractice(true)}
              className="bg-white text-[#1d366b] hover:bg-slate-100 font-semibold flex-shrink-0"
              data-testid="open-private-practice-btn"
            >
              Activar ahora
            </Button>
          </div>
        )}
        {/* Banner del Consultorio Privado: sólo aparece cuando el médico está
            viendo "Todas las instituciones" o el tenant "Consultorio Privado".
            Al mirar un tenant institucional (Fleni, Santa Fe, etc.) se oculta,
            porque el banner es contextual a la práctica privada. */}
        {privatePractice?.active && (institutionFilter === 'all' || activeTenant?.is_private_practice) && (
          <div className="bg-white rounded-xl border border-emerald-200 p-4 mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3" data-testid="private-practice-active-strip">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 bg-emerald-50 rounded-full flex items-center justify-center flex-shrink-0">
                <CheckCircle2 className="h-5 w-5 text-emerald-600" />
              </div>
              <p className="text-sm text-slate-700">
                <strong>Consultorio privado activo</strong>
              </p>
            </div>
            <div className="flex gap-2 flex-shrink-0 flex-wrap">
              <Button
                variant="outline"
                size="sm"
                onClick={async () => {
                  const link = `${window.location.origin}${privatePractice.link_path}`;
                  await navigator.clipboard.writeText(link);
                  toast.success("Enlace privado copiado");
                }}
                data-testid="copy-private-link-strip-btn"
              >
                <Copy className="h-3.5 w-3.5 mr-1.5" />
                Copiar enlace
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowPrivatePractice(true)}
                data-testid="config-private-practice-btn"
              >
                Configurar
              </Button>
              <Button
                size="sm"
                onClick={() => setShowEarnings(true)}
                className="bg-[#1d366b] hover:bg-[#16295a] text-white"
                data-testid="view-earnings-strip-btn"
              >
                <Wallet className="h-3.5 w-3.5 mr-1.5" />
                Ver mis cobros
              </Button>
            </div>
          </div>
        )}

        {/* ===== BANDEJA DE CONSULTAS ===== */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 gap-3">
          <div className="flex items-center gap-3 flex-wrap">
            <h2 className="text-xl font-bold text-slate-800">Bandeja de Consultas</h2>
            {pendingCount > 0 && (
              <Button
                size="sm"
                onClick={() => {
                  const firstPending = sortedConsults.find(c => c.status === 'Paid');
                  if (firstPending) navigate(`/consult/${firstPending.id}`);
                }}
                className="bg-primary hover:bg-primary/90 h-9"
                data-testid="respond-consults-btn"
              >
                <Play className="h-4 w-4 mr-2" />
                Responder cola
                <Badge className="ml-2 bg-white/20 text-white border-0">{pendingCount}</Badge>
              </Button>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {/* Institution filter - only show if doctor has multiple institutions */}
            {myInstitutions.length > 1 && (
              <select
                value={institutionFilter}
                onChange={(e) => setInstitutionFilter(e.target.value)}
                className="text-sm border border-slate-200 rounded-md px-3 py-1.5 bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                data-testid="institution-filter-select"
              >
                <option value="all">Todas las instituciones</option>
                {myInstitutions.map((inst) => (
                  <option key={inst.id} value={inst.id}>{inst.name}</option>
                ))}
              </select>
            )}
            <Button
              variant={filter === 'pending' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilter('pending')}
              data-testid="filter-pending-btn"
            >
              Pendientes
            </Button>
            <Button
              variant={filter === 'responded' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilter('responded')}
              data-testid="filter-responded-btn"
            >
              Respondidas
            </Button>
            <Button
              variant={filter === 'all' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilter('all')}
              data-testid="filter-all-btn"
            >
              Todas
            </Button>
            {filter === 'responded' && (
              <select
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
                className="h-9 rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-400 ml-auto"
                data-testid="responded-sort-select"
                aria-label="Ordenar respondidas por fecha"
              >
                <option value="recent">Más recientes primero</option>
                <option value="oldest">Más antiguas primero</option>
              </select>
            )}
          </div>
        </div>

        {/* Inbox filters row: search by patient, category chips, red-flags toggle.
            All client-side. Renders a summary + clear button once any filter is active. */}
        <div className="bg-white border border-slate-200 rounded-xl p-3 mb-4 flex flex-col gap-3" data-testid="inbox-filters">
          <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
              <input
                type="text"
                value={patientSearch}
                onChange={(e) => setPatientSearch(e.target.value)}
                placeholder="Buscar por nombre o DNI"
                className="w-full text-sm border border-slate-200 rounded-md pl-8 pr-8 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500"
                data-testid="inbox-search-input"
              />
              {patientSearch && (
                <button
                  type="button"
                  onClick={() => setPatientSearch('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  aria-label="Limpiar búsqueda"
                  data-testid="inbox-search-clear"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>

            <label className="inline-flex items-center gap-2 text-sm text-slate-700 select-none" data-testid="inbox-redflags-toggle-wrapper">
              <input
                type="checkbox"
                checked={redFlagsOnly}
                onChange={(e) => setRedFlagsOnly(e.target.checked)}
                className="h-4 w-4"
                data-testid="inbox-redflags-toggle"
              />
              <AlertTriangle className="h-4 w-4 text-red-500" />
              Solo con alertas rojas
            </label>

            {hasActiveInboxFilter && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearInboxFilters}
                className="text-slate-500 sm:ml-auto"
                data-testid="clear-inbox-filters"
              >
                <X className="h-4 w-4 mr-1" />
                Limpiar filtros ({filteredConsults.length}/{sortedConsults.length})
              </Button>
            )}
          </div>

          <div className="flex flex-wrap gap-1.5" data-testid="inbox-category-chips">
            {CATEGORY_OPTIONS.map((cat) => {
              const active = categoryFilter.includes(cat.id);
              return (
                <button
                  key={cat.id}
                  type="button"
                  onClick={() => toggleCategoryFilter(cat.id)}
                  className={`text-xs px-3 py-1 rounded-full border transition-colors ${
                    active
                      ? 'bg-primary text-white border-primary'
                      : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'
                  }`}
                  data-testid={`inbox-category-chip-${cat.id}`}
                >
                  {cat.label}
                </button>
              );
            })}
          </div>
        </div>

        {filteredConsults.length === 0 ? (
          <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
            <CheckCircle2 className="h-16 w-16 text-green-300 mx-auto mb-4" />
            <p className="text-slate-600 text-lg">
              {hasActiveInboxFilter
                ? 'Sin resultados para los filtros aplicados'
                : filter === 'responded'
                  ? 'Todavía no tenés consultas respondidas'
                  : filter === 'all'
                    ? 'No tenés consultas todavía'
                    : '¡No hay consultas pendientes!'}
            </p>
            <p className="text-slate-400 text-sm mt-1">
              {hasActiveInboxFilter
                ? 'Probá quitar filtros o ampliar la búsqueda.'
                : filter === 'responded'
                  ? 'Cuando respondas una consulta, aparecerá acá.'
                  : filter === 'all'
                    ? 'Las consultas de tus pacientes aparecerán acá.'
                    : 'Todas las consultas han sido respondidas'}
            </p>
            {hasActiveInboxFilter && (
              <Button variant="outline" size="sm" className="mt-4" onClick={clearInboxFilters} data-testid="clear-inbox-filters-empty">
                Limpiar filtros
              </Button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="consults-grid">
            {filteredConsults.map((consult) => {
              const priority = getPriorityStatus(consult);
              
              return (
                <div
                  key={consult.id}
                  onClick={() => navigate(`/consult/${consult.id}`)}
                  className={`bg-white rounded-xl border border-slate-200 p-5 cursor-pointer transition-all hover:shadow-md hover:border-slate-300 ${priority?.className || ''}`}
                  data-testid={`consult-card-${consult.id}`}
                >
                  {/* Header con estado y fecha */}
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2 flex-wrap">
                      {getStatusBadge(consult.status)}
                      {priority && (
                        <span className={`text-xs font-medium ${
                          priority.color === 'red' ? 'text-red-600' :
                          priority.color === 'yellow' ? 'text-amber-600' :
                          'text-green-600'
                        }`}>
                          {priority.label}
                        </span>
                      )}
                      {consult.high_clinical_priority && (
                        <span
                          data-testid={`high-priority-badge-${consult.id}`}
                          className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide bg-amber-100 text-amber-900 border border-amber-300 rounded px-1.5 py-0.5 font-semibold"
                          title="El paciente reportó un síntoma de alarma reciente que ya pasó"
                        >
                          <AlertTriangle className="h-3 w-3" />
                          Síntoma reciente
                        </span>
                      )}
                      {consult.is_third_party && (
                        <span
                          data-testid={`third-party-badge-${consult.id}`}
                          className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide bg-blue-100 text-blue-900 border border-blue-300 rounded px-1.5 py-0.5 font-semibold"
                          title="Consulta realizada por un tercero (familiar/cuidador) en nombre del paciente"
                        >
                          <Users className="h-3 w-3" />
                          Por tercero
                        </span>
                      )}
                    </div>
                    <span className="text-xs text-slate-400">
                      {filter === 'responded' && consult.responded_at
                        ? `Respondida ${new Date(consult.responded_at).toLocaleDateString('es-AR')}`
                        : new Date(consult.created_at).toLocaleDateString('es-AR')}
                    </span>
                  </div>

                  {/* Nombre paciente */}
                  <h3 className="font-semibold text-slate-800 mb-1">{consult.patient_name}</h3>
                  
                  {/* Institution origin badge - only show if multiple institutions */}
                  {myInstitutions.length > 1 && consult.institution_id && (
                    <span className="inline-block text-xs bg-slate-100 text-slate-500 px-2 py-0.5 rounded mb-1" data-testid={`consult-origin-${consult.id}`}>
                      {myInstitutions.find(i => i.id === consult.institution_id)?.name || 'Otra'}
                    </span>
                  )}
                  
                  {/* Tipo de consulta */}
                  <p className="text-sm text-slate-500 mb-2 flex items-center gap-2">
                    {consult.category === 'recetas' && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-fuchsia-100 text-fuchsia-700 uppercase tracking-wide" data-testid={`receta-badge-${consult.id}`}>
                        Receta
                      </span>
                    )}
                    {consult.category !== 'recetas' && getCategoryLabel(consult.category)}
                  </p>

                  {/* Motivo (truncado) */}
                  <p className="text-sm text-slate-600 line-clamp-2 mb-3">
                    {consult.consult_reason}
                  </p>

                  {/* Tiempo restante */}
                  {consult.sla_deadline && consult.status === 'Paid' && (
                    <div className="flex items-center text-sm pt-2 border-t border-slate-100">
                      <Clock className="h-4 w-4 mr-2 text-slate-400" />
                      {getTimeRemaining(consult.sla_deadline)}
                    </div>
                  )}

                  {/* Indicador de triaje */}
                  {consult.triage_metadata?.result?.nivel && consult.triage_metadata.result.nivel > 1 && (
                    <div className={`mt-2 p-2 rounded flex items-center justify-between text-xs ${
                      consult.triage_metadata.result.nivel === 3 ? 'bg-orange-50 text-orange-700' :
                      'bg-yellow-50 text-yellow-700'
                    }`}>
                      <span className="flex items-center">
                        <Activity className="h-3 w-3 mr-1" />
                        Triaje N{consult.triage_metadata.result.nivel}
                      </span>
                      <span className="font-medium">
                        {consult.triage_metadata.result.nivel === 3 ? 'Alta Prioridad' : 'Prioridad Media'}
                      </span>
                    </div>
                  )}

                  {/* Indicador de esperando info */}
                  {consult.status === 'AWAITING_INFO' && consult.info_request_deadline && (
                    <div className="mt-2 p-2 rounded bg-orange-50 border border-orange-200 text-xs">
                      <div className="flex items-center justify-between text-orange-700">
                        <span className="flex items-center">
                          <Timer className="h-3 w-3 mr-1" />
                          Esperando respuesta del paciente
                        </span>
                        <span className="font-medium">
                          {(() => {
                            const deadline = new Date(consult.info_request_deadline);
                            const now = new Date();
                            const diffDays = Math.ceil((deadline - now) / (1000 * 60 * 60 * 24));
                            if (diffDays <= 0) return 'Vencido';
                            return `${diffDays} día${diffDays > 1 ? 's' : ''} restantes`;
                          })()}
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Indicador de info recibida */}
                  {consult.status === 'INFO_RECEIVED' && (
                    <div className="mt-2 p-2 rounded bg-violet-50 border border-violet-200 text-xs animate-pulse">
                      <div className="flex items-center text-violet-700">
                        <MessageSquarePlus className="h-3 w-3 mr-1" />
                        <span className="font-medium">¡Paciente respondió! Requiere tu atención</span>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* ===== GRÁFICOS (máximo 4) ===== */}
        {stats && (
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
            
            {/* Gráfico 1: Distribución por tipo de consulta */}
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h3 className="text-sm font-semibold text-slate-700 mb-4">Distribución por tipo de consulta</h3>
              <div className="space-y-3">
                {Object.entries(stats.category_distribution || {}).map(([category, count]) => {
                  const total = Object.values(stats.category_distribution).reduce((a, b) => a + b, 0);
                  const percentage = total > 0 ? Math.round((count / total) * 100) : 0;
                  const colors = {
                    'medicacion': 'bg-blue-500',
                    'recetas': 'bg-fuchsia-500',
                    'resultados': 'bg-green-500',
                    'seguimiento': 'bg-purple-500',
                    'documentacion': 'bg-amber-500',
                    'otra': 'bg-slate-400'
                  };
                  
                  return (
                    <div key={category}>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-slate-600">{getCategoryLabel(category)}</span>
                        <span className="font-medium text-slate-800">{count} ({percentage}%)</span>
                      </div>
                      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div 
                          className={`h-full ${colors[category] || 'bg-slate-400'} rounded-full transition-all`}
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
                {Object.keys(stats.category_distribution || {}).length === 0 && (
                  <p className="text-slate-400 text-sm text-center py-4">Sin datos disponibles</p>
                )}
              </div>
            </div>

            {/* Gráfico 2: Distribución de tiempos de respuesta */}
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h3 className="text-sm font-semibold text-slate-700 mb-4">Tiempo de respuesta</h3>
              <div className="space-y-3">
                {[
                  { key: 'menos_2h', label: 'Menos de 2 horas', color: 'bg-green-500' },
                  { key: '2_6h', label: '2 a 6 horas', color: 'bg-blue-500' },
                  { key: '6_12h', label: '6 a 12 horas', color: 'bg-amber-500' },
                  { key: '12_24h', label: '12 a 24 horas', color: 'bg-orange-500' },
                  { key: 'mas_24h', label: 'Más de 24 horas', color: 'bg-red-500' }
                ].map(({ key, label, color }) => {
                  const count = stats.time_distribution?.[key] || 0;
                  const total = Object.values(stats.time_distribution || {}).reduce((a, b) => a + b, 0);
                  const percentage = total > 0 ? Math.round((count / total) * 100) : 0;
                  
                  return (
                    <div key={key}>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-slate-600">{label}</span>
                        <span className="font-medium text-slate-800">{count}</span>
                      </div>
                      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div 
                          className={`h-full ${color} rounded-full transition-all`}
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Gráfico 3: Resultado de consultas */}
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h3 className="text-sm font-semibold text-slate-700 mb-4">Resultado de las consultas</h3>
              <div className="space-y-4">
                {[
                  { key: 'resolved_async', label: 'Resueltas asincrónicamente', color: 'bg-green-500', icon: CheckCircle2 },
                  { key: 'escalated_teleconsult', label: 'Escaladas a teleconsulta', color: 'bg-purple-500', icon: TrendingUp },
                  { key: 'escalated_presential', label: 'Escaladas a presencial', color: 'bg-blue-500', icon: ArrowRight }
                ].map(({ key, label, color, icon: Icon }) => {
                  const count = stats.resolution_stats?.[key] || 0;
                  const total = Object.values(stats.resolution_stats || {}).reduce((a, b) => a + b, 0);
                  const percentage = total > 0 ? Math.round((count / total) * 100) : 0;
                  
                  return (
                    <div key={key} className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg ${color} bg-opacity-10 flex items-center justify-center`}>
                        <Icon className={`h-5 w-5 ${color.replace('bg-', 'text-')}`} />
                      </div>
                      <div className="flex-1">
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-600">{label}</span>
                          <span className="font-semibold text-slate-800">{count}</span>
                        </div>
                        <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden mt-1">
                          <div 
                            className={`h-full ${color} rounded-full`}
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Gráfico 4: Evolución mensual (simplificado) */}
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h3 className="text-sm font-semibold text-slate-700 mb-4">Evolución mensual</h3>
              {Object.keys(stats.monthly_evolution || {}).length > 0 ? (
                <div className="space-y-2">
                  {Object.entries(stats.monthly_evolution).slice(-6).map(([month, categories]) => {
                    const total = Object.values(categories).reduce((a, b) => a + b, 0);
                    const maxTotal = Math.max(...Object.values(stats.monthly_evolution).map(c => 
                      Object.values(c).reduce((a, b) => a + b, 0)
                    ));
                    const percentage = maxTotal > 0 ? (total / maxTotal) * 100 : 0;
                    
                    return (
                      <div key={month} className="flex items-center gap-3">
                        <span className="text-xs text-slate-500 w-16">{month}</span>
                        <div className="flex-1 h-6 bg-slate-100 rounded overflow-hidden flex">
                          {Object.entries(categories).map(([cat, count]) => {
                            const catPercentage = total > 0 ? (count / total) * percentage : 0;
                            const colors = {
                              'medicacion': 'bg-blue-500',
                              'recetas': 'bg-fuchsia-500',
                              'resultados': 'bg-green-500',
                              'seguimiento': 'bg-purple-500',
                              'documentacion': 'bg-amber-500',
                              'otra': 'bg-slate-400'
                            };
                            return (
                              <div 
                                key={cat}
                                className={`h-full ${colors[cat] || 'bg-slate-400'}`}
                                style={{ width: `${catPercentage}%` }}
                                title={`${getCategoryLabel(cat)}: ${count}`}
                              />
                            );
                          })}
                        </div>
                        <span className="text-xs font-medium text-slate-700 w-8 text-right">{total}</span>
                      </div>
                    );
                  })}
                  <div className="flex gap-3 mt-4 flex-wrap">
                    {[
                      { cat: 'medicacion', label: 'Tratamiento', color: 'bg-blue-500' },
                      { cat: 'recetas', label: 'Recetas', color: 'bg-fuchsia-500' },
                      { cat: 'resultados', label: 'Resultados', color: 'bg-green-500' },
                      { cat: 'seguimiento', label: 'Seguimiento', color: 'bg-purple-500' },
                      { cat: 'documentacion', label: 'Documentación', color: 'bg-amber-500' }
                    ].map(({ cat, label, color }) => (
                      <div key={cat} className="flex items-center gap-1.5 text-xs text-slate-500">
                        <div className={`w-2.5 h-2.5 rounded ${color}`} />
                        {label}
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <p className="text-slate-400 text-sm text-center py-8">Sin datos de evolución</p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DoctorDashboard;
